/**
 * New-UX "Now" divider + pending-schedule rows for the Experience Log section.
 * useUpcomingScheduleLogItems produces the exact same LogItem shape ExperienceLog's
 * history rows use, so ExperienceLog can render both through the identical LogTable
 * (same layout) and pass both through the identical filters.
 */
import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  ExperienceEventType,
  IExperience,
  IExperienceTemplate
} from '@dadohr/shared/domainTypes/experience';
import {
  Schedule,
  ScheduleStatus,
  ScheduleType,
  isExperienceLevelSchedule,
  isExperienceMessageLevelSchedule,
  isTaskLevelSchedule
} from '@dadohr/shared/domainTypes/schedules';
import { IAutomationSchedule } from '@dadohr/shared/domainTypes/automations';
import { FS } from '@dadohr/shared/versions';
import styled from '@emotion/styled';
import { ArrowDown } from 'lucide-react';
import React, { useLayoutEffect, useState } from 'react';
import { useCollection } from 'react-firebase-hooks/firestore';
import { LogItem } from '../../../../../components/LogTable';
import { UnsafeTFunction, useUnsafeTranslation } from '../../../../../i18next';
import { toAutomationScheduleDoc } from '../../../../../services/automations';
import {
  getMessageTemplateById,
  getTaskTemplateForTaskId
} from '../../../../../services/experiences/template';
import { store, useMappedLoadingValue } from '../../../../../services/firebase';
import { lightMdToJsx } from '../../../../../services/formatter';
import { toScheduleDoc } from '../../../../../services/schedules/documents';
import { ActorDescriptor } from '../../../components/Actors';
import { NewUxButton } from '../../../components/Builders/newux/kit/Button';
import { tokens } from '../../../components/Builders/newux/kit/tokens';
import { getItemDisplayName } from '../../../helpers/adminName';

const { color, font } = tokens;

export const NowDivider = styled('div')`
  display: flex;
  align-items: center;
  gap: ${tokens.space(2)};
  margin: ${tokens.space(5)} 0;
  color: ${color.textSecondary};
  font-size: ${font.variant.h7.fontSize}px;
  font-weight: ${font.variant.h7.fontWeight};
  text-transform: ${font.variant.h7.textTransform};
  letter-spacing: 0.04em;
  &::before,
  &::after {
    content: '';
    flex: 1 1 auto;
    height: 1px;
    background: ${color.borderSubtle};
  }
`;

const JumpToNowLinkButton: React.FC<
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'>
> = (
  props
) => (
  <NewUxButton
    variant="text"
    color="primary"
    size="small"
    style={{ marginBottom: tokens.space(3) }}
    {...props}
  />
);

/**
 * Tracks an element's rendered height, live — used to offset the "Now" divider's
 * scroll-margin-top by the sticky log header's actual height, so scrolling/jumping to it
 * doesn't leave it hidden underneath the header.
 */
export const useElementHeight = (ref: React.RefObject<HTMLElement>): number => {
  const [height, setHeight] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      setHeight(el.getBoundingClientRect().height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return height;
};

const getActorLabel = (
  actorDescriptors: { [actorKey: string]: ActorDescriptor },
  actorKey: string,
  t: UnsafeTFunction
) => actorDescriptors[actorKey || '']?.label || t('Unknown');

/**
 * Maps pending (not-yet-run) task/message schedules into the same LogItem shape as
 * ExperienceLog's history rows, deduping schedules that would render identical row
 * content — a task/message scheduled on multiple channels (e.g. email + Slack) produces
 * one PENDING schedule doc per channel, all with the same item/actor/time, so without
 * deduping each would render as a separate row. Pulled out of the Firestore-subscribing
 * hook below so it's testable as a plain function over already-fetched docs.
 */
export const toTaskAndMessageLogItems = (
  schedules: Doc<Schedule>[],
  tpl: Doc<IExperienceTemplate>,
  exp: Doc<IExperience>,
  actorDescriptors: { [actorKey: string]: ActorDescriptor },
  t: UnsafeTFunction
): LogItem[] => {
  const dedup = new Map<string, LogItem>();
  schedules.forEach((x: Doc<Schedule>) => {
    let type: ExperienceEventType;
    let actorKey: string;
    let node: React.ReactNode;
    let searchValue: string;

    if (isExperienceLevelSchedule(x)) {
      type = ExperienceEventType.EXP_START;
      actorKey = 'OTHER';
      node = t('Experience starts');
      searchValue = t('Experience starts');
    } else if (isExperienceMessageLevelSchedule(x)) {
      const msgTpl = getMessageTemplateById(tpl, x.data.msgId);
      if (!msgTpl) return;
      const name = getItemDisplayName(msgTpl);
      actorKey = x.data.actor;
      const actor = getActorLabel(actorDescriptors, actorKey, t);
      // i18next treats ':' inside a t() key as a namespace separator, so the colon
      // (and the item name it introduces) is appended outside the translated text —
      // same convention as the history rows' toLogEvent (e.g. TASK_START).
      node = lightMdToJsx(
        `${t('Message for **{{ actor }}** sends', { actor })}: **${name}**`
      );
      searchValue = t('Message for {{ actor }} sends {{ name }}', { actor, name });
      type = ExperienceEventType.EXP_MESSAGE;
    } else if (isTaskLevelSchedule(x)) {
      if (x.data.type === ScheduleType.TASK_END) return;
      const taskTpl = getTaskTemplateForTaskId(tpl, exp, x.data.taskId);
      if (!taskTpl) return;
      const name = getItemDisplayName(taskTpl);
      actorKey = x.data.actor;
      const actor = getActorLabel(actorDescriptors, actorKey, t);
      const isReminder =
        x.data.type === ScheduleType.TASK_REMINDER ||
        x.data.type === ScheduleType.TASK_LATE_REMINDER;
      if (isReminder) {
        const isOverdue = x.data.type === ScheduleType.TASK_LATE_REMINDER;
        node = lightMdToJsx(
          `${
            isOverdue
              ? t('Overdue reminder sends for **{{ actor }}** task', { actor })
              : t('Pre-deadline reminder sends for **{{ actor }}** task', { actor })
          }: **${name}**`
        );
        searchValue = `${
          isOverdue
            ? t('Overdue reminder sends for {{ actor }} task', { actor })
            : t('Pre-deadline reminder sends for {{ actor }} task', { actor })
        } ${name}`;
        type = isOverdue
          ? ExperienceEventType.TASK_LATE_REMINDER
          : ExperienceEventType.TASK_REMINDER;
      } else {
        node = lightMdToJsx(`${t('Task for **{{ actor }}** starts', { actor })}: **${name}**`);
        searchValue = t('Task for {{ actor }} starts {{ name }}', { actor, name });
        type = ExperienceEventType.TASK_START;
      }
    } else {
      return;
    }

    const dedupeKey = [type, actorKey, searchValue, x.data.at.toMillis()].join('---');
    if (!dedup.has(dedupeKey)) {
      dedup.set(dedupeKey, {
        key: dedupeKey,
        at: x.data.at,
        d: node,
        searchValue,
        executor: null,
        actor: actorKey,
        type
      });
    }
  });
  return Array.from(dedup.values());
};

/** Same idea as toTaskAndMessageLogItems, for pending automation schedules. */
export const toAutomationLogItems = (
  schedules: Doc<IAutomationSchedule>[],
  tpl: Doc<IExperienceTemplate>,
  t: UnsafeTFunction
): LogItem[] => {
  const dedup = new Map<string, LogItem>();
  schedules.forEach((x: Doc<IAutomationSchedule>) => {
    const name =
      tpl.data.automations.find((a) => a.id === x.data.automationTemplateId)?.title || '';
    const node = lightMdToJsx(`${t('Automation runs')}: **${name}**`);
    const searchValue = t('Automation runs {{ name }}', { name });
    const dedupeKey = ['AUTOMATION_RAN', searchValue, x.data.at.toMillis()].join('---');
    if (!dedup.has(dedupeKey)) {
      dedup.set(dedupeKey, {
        key: dedupeKey,
        at: x.data.at,
        d: node,
        searchValue,
        executor: null,
        actor: 'AUTOMATION',
        type: ExperienceEventType.AUTOMATION_RAN
      });
    }
  });
  return Array.from(dedup.values());
};

export const useUpcomingScheduleLogItems = (
  tpl: Doc<IExperienceTemplate>,
  exp: Doc<IExperience>,
  actorDescriptors: { [actorKey: string]: ActorDescriptor },
  t: UnsafeTFunction,
  /** Skips both queries (e.g. for the classic tab, which has its own separate schedules UI). */
  enabled: boolean
): LogItem[] | undefined => {
  const [taskAndMessageRows] = useMappedLoadingValue(
    useCollection(
      enabled
        ? store()
            .collection(FS.schedules)
            .where('orgId', '==', tpl.data.orgId)
            .where('experienceId', '==', exp.id)
            .where('status', 'in', [ScheduleStatus.PENDING, ScheduleStatus.RETRY])
        : undefined
    ),
    (s) =>
      toTaskAndMessageLogItems(s.docs.map(toScheduleDoc), tpl, exp, actorDescriptors, t)
  );

  const [automationRows] = useMappedLoadingValue(
    useCollection(
      enabled
        ? store()
            .collection(FS.automationSchedules)
            .where('orgId', '==', tpl.data.orgId)
            .where('experienceId', '==', exp.id)
            .where('status', 'in', [ScheduleStatus.PENDING, ScheduleStatus.RETRY])
        : undefined
    ),
    (s) => toAutomationLogItems(s.docs.map(toAutomationScheduleDoc), tpl, t)
  );

  if (!taskAndMessageRows || !automationRows) return undefined;
  return [...taskAndMessageRows, ...automationRows].sort(
    (a, b) => a.at.toMillis() - b.at.toMillis()
  );
};

export const JumpToNowLink: React.FC<{
  dividerRef: React.RefObject<HTMLDivElement>;
}> = ({ dividerRef }) => {
  const { t } = useUnsafeTranslation();
  return (
    <JumpToNowLinkButton
      type="button"
      onClick={() =>
        dividerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    >
      <ArrowDown size={16} /> {t('Jump to now')}
    </JumpToNowLinkButton>
  );
};
